import * as echarts from "echarts/core";
import type { CallbackDataParams } from "echarts/types/dist/shared";
import { getUnitCategoryName, type Locale } from "verdi-common/i18n";
import { formatTooltipValue } from "../../format";
import type { GetDisplayInfo } from "../../units/use-get-display-info";
import type { PreparedSeries } from "../prepare-series";

export type TooltipContext = {
    seriesByStreamId: Map<number, PreparedSeries>;
    getDisplayInfo: GetDisplayInfo;
    showDeviceName: boolean;
    showSensorClusterName: boolean;
    showSensorElementName: boolean;
    locale: Locale;
};

const buildTooltipRowHtml = (data: CallbackDataParams, context: TooltipContext): string => {
    const { getDisplayInfo, locale, seriesByStreamId, showDeviceName, showSensorClusterName, showSensorElementName } =
        context;

    const preparedSeries = seriesByStreamId.get(Number(data.seriesId));
    const markerColor = String(data.color);
    const unitCategory = preparedSeries?.unitCategory ?? "";
    const category = getUnitCategoryName(unitCategory, locale);
    const value = (Array.isArray(data.value) ? data.value[1] : data.value) as number | null;
    const unitSuffix = unitCategory ? getDisplayInfo(unitCategory).symbol : "";
    const formattedValue =
        value === null || typeof value === "undefined"
            ? "—"
            : [formatTooltipValue(value), unitSuffix].filter(Boolean).join(" ");

    // Most channels are named after their metric, and the row already leads with that.
    const elementName = preparedSeries?.sensorElementName === category ? null : preparedSeries?.sensorElementName;

    const subLabel = [
        showDeviceName ? preparedSeries?.name : null,
        showSensorClusterName ? preparedSeries?.sensorClusterName : null,
        showSensorElementName ? elementName : null,
    ]
        .filter(Boolean)
        .join(" · ");

    // biome-ignore format: keep tooltip HTML indentation
    return (
        `<div style="display: flex; justify-content: space-between; gap: 16px;">` +
            `<div style="display: flex; align-items: center; gap: 6px;">` +
                `<div style="width: 8px; height: 8px; border-radius: 9999px; background-color: ${markerColor};"></div>` +
                `<div style="display: flex; flex-direction: column; gap: 2px; align-items: flex-start; justify-content: flex-start; flex: 0; flex-grow: 0;">` +
                    `<div>${echarts.format.encodeHTML(category)}</div>` +
                    `${subLabel ? `<div style="font-size: 10px; line-height: 12px; color: gray;">${echarts.format.encodeHTML(subLabel)}</div>` : ""}` +
                `</div>` +
            `</div>` +
            `<div style="display: flex; justify-content: flex-end;">${formattedValue}</div>` +
        `</div>`
    );
};

/** One row per hovered series: marker, unit category over the names that tell the series apart, then the value. */
export function renderTooltipHtml(params: CallbackDataParams[], tooltipContext: TooltipContext): string {
    const rows = params.map((p) => buildTooltipRowHtml(p, tooltipContext)).join("");

    return `<div style="display: flex; flex-direction: column; gap: 6px;">${rows}</div>`;
}
